import { getAutoCompleteSuggestions } from '@griply/utils';
import React, { forwardRef, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Check, Minus, Plus, Search, Tag, X } from 'shared/assets/icons';
import { Icon } from 'shared/components/ui/icon';
import {
  Colors,
  PopupMenu,
  PopupMenuButton,
  PopupMenuInput,
  PopupMenuList,
  PopupMenuListItem,
  PopupMenuNoResults,
} from 'shared/components/ui/popup-menu';
import { useForwardedRef } from 'shared/hooks/use-forwarded-ref';
import { usePopupMenuArrowNavigation } from 'shared/hooks/use-popup-menu-arrow-navigation';
import { ID } from 'shared/types/id';
import { NewTagWithID, Tag as TagType } from 'shared/types/tag';

import * as Styled from './tag-search-menu.style';

export type TagSearchMenuProps = Omit<
  React.ComponentProps<typeof PopupMenu>,
  'ref' | 'children'
> & {
  tags: (TagType | NewTagWithID)[];
  value?: ID[] | null;
  indeterminateValue?: ID[] | null;
  onChange: (ids: ID[]) => void;
  onClear?: () => void;
  onCreate?: (name: string) => void;
  hideCreateButton?: boolean;
};

export const TagSearchMenu = forwardRef<HTMLDivElement, TagSearchMenuProps>(
  (
    {
      tags,
      value,
      indeterminateValue,
      onChange,
      onClear,
      onCreate,
      hideCreateButton,
      ...rest
    },
    ref,
  ) => {
    const containerRef = useForwardedRef(ref);
    const { t } = useTranslation();
    const inputRef = useRef<HTMLInputElement>(null);
    const [containerHeight, setContainerHeight] = useState<number>();

    const [searchValue, setSearchValue] = useState('');

    const _onCreate = (e: React.MouseEvent) => {
      e.preventDefault();
      onCreate?.(searchValue);
      setSearchValue('');
    };

    const onChangeInput = (e: React.ChangeEvent<HTMLInputElement>) =>
      setSearchValue(e.target.value);

    const _onClear = () => {
      if (onClear) {
        onClear();
        return;
      }
      onChange([]);
    };

    const _onChange = (id: ID) => {
      const oldValue = value ?? [];
      const newValue = oldValue.filter((tagId) => tagId !== id);
      if (oldValue.length === newValue.length) {
        newValue.push(id);
      }

      onChange(newValue);
    };

    const tagSuggestions = useMemo(
      () => getAutoCompleteSuggestions(searchValue, tags, ['name']),
      [searchValue, tags],
    );

    usePopupMenuArrowNavigation({ containerRef, inputRef });

    useEffect(() => {
      setContainerHeight(
        Math.max(
          containerRef.current?.clientHeight ?? 0,
          Styled.popupMenuMinHeight,
        ),
      );
    }, [containerRef]);

    return (
      <Styled.PopupMenu
        ref={containerRef}
        $height={containerHeight}
        {...rest}
        initialFocus={inputRef}
      >
        <PopupMenuList hasBottomBorder>
          <PopupMenuListItem>
            <PopupMenuInput
              ref={inputRef}
              startIcon={Search}
              value={searchValue}
              placeholder={t(
                onCreate
                  ? 'task.tags.search-or-create.placeholder'
                  : 'task.tags.search.placeholder',
              )}
              onChange={onChangeInput}
            />
          </PopupMenuListItem>
        </PopupMenuList>

        <PopupMenuList hasBottomBorder={!!value?.length}>
          {tagSuggestions.map(({ id, name, color }) => {
            const onSelectItem = () => _onChange(id);

            return (
              <PopupMenuListItem key={id}>
                <PopupMenuButton
                  start={
                    <Styled.IconContainer $color={color}>
                      <Icon icon={Tag} />
                    </Styled.IconContainer>
                  }
                  onClick={onSelectItem}
                  end={
                    value?.includes(id) ? (
                      <Styled.SelectedIconContainer>
                        <Icon icon={Check} />
                      </Styled.SelectedIconContainer>
                    ) : indeterminateValue?.includes(id) ? (
                      <Styled.SelectedIconContainer>
                        <Icon icon={Minus} />
                      </Styled.SelectedIconContainer>
                    ) : undefined
                  }
                  tabIndex={0}
                  overflowEllipsis
                >
                  {name}
                </PopupMenuButton>
              </PopupMenuListItem>
            );
          })}
          {!!onCreate && (!hideCreateButton || !!searchValue) && (
            <PopupMenuListItem>
              <PopupMenuButton
                start={<Icon icon={Plus} />}
                startColor={Colors.Primary}
                textColor={Colors.Primary}
                onClick={_onCreate}
                tabIndex={0}
              >
                {searchValue || t('forms.tag.create.title')}
              </PopupMenuButton>
            </PopupMenuListItem>
          )}
        </PopupMenuList>

        {!tagSuggestions.length &&
          (!onCreate || (hideCreateButton && !!searchValue)) &&
          !!searchValue && (
            <PopupMenuNoResults>
              {t('general.labels.no-results')}
            </PopupMenuNoResults>
          )}

        {(!!value?.length || !!indeterminateValue?.length) && (
          <PopupMenuList>
            <PopupMenuListItem>
              <PopupMenuButton
                start={<Icon icon={X} />}
                startColor={Colors.Error}
                textColor={Colors.Error}
                onClick={_onClear}
                tabIndex={0}
              >
                {t('task.tags.options.remove.label', {
                  count:
                    (value?.length ?? 0) + (indeterminateValue?.length ?? 0),
                })}
              </PopupMenuButton>
            </PopupMenuListItem>
          </PopupMenuList>
        )}
      </Styled.PopupMenu>
    );
  },
);
